import type { ReactNode } from 'react'

import { IconCheck } from '@tabler/icons-react'

import {
  ResponsiveDialogBody,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@/components/common/responsive-dialog'
import { Badge } from '@/components/sui/badge'
import { Button } from '@/components/sui/button'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { PLAN_DISPLAY_NAMES, PLAN_VISUAL_CONFIG } from '@/lib/plans'
import { cn } from '@/lib/utils'

import locales from '../locales'
import type { FeatureKey } from '../plan-finder.utils'
import { FEATURE_KEYS, FEATURE_MIN_PLAN } from '../plan-finder.utils'

interface FeaturesStepProps {
  progressBar: ReactNode
  selectedFeatures: Array<FeatureKey>
  onToggleFeature: (feature: FeatureKey) => void
  onBack: () => void
  onNext: () => void
}

export function FeaturesStep({
  progressBar,
  selectedFeatures,
  onToggleFeature,
  onBack,
  onNext,
}: FeaturesStepProps) {
  const { t } = useComponentTranslation(locales)

  return (
    <>
      <ResponsiveDialogHeader>
        <ResponsiveDialogTitle>{t('title')}</ResponsiveDialogTitle>
        <ResponsiveDialogDescription>
          {t('steps.description')}
        </ResponsiveDialogDescription>
      </ResponsiveDialogHeader>

      <ResponsiveDialogBody>
        <div className="space-y-4">
          {progressBar}

          <div>
            <p className="text-sm font-medium">
              {t('steps.features.question')}
            </p>
            <p className="text-muted-foreground mt-0.5 text-xs">
              {t('steps.features.hint')}
            </p>
          </div>

          <div className="space-y-2">
            {FEATURE_KEYS.map((featureKey) => {
              const isSelected = selectedFeatures.includes(featureKey)
              const minPlan = FEATURE_MIN_PLAN[featureKey]
              const visual = PLAN_VISUAL_CONFIG[minPlan]
              const PlanIcon = visual.icon

              return (
                <button
                  key={featureKey}
                  type="button"
                  className={cn(
                    'flex w-full items-start gap-3 rounded-lg border px-4 py-3 text-left text-sm transition-colors',
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50 hover:bg-muted/50',
                    'cursor-pointer',
                  )}
                  onClick={() => onToggleFeature(featureKey)}
                >
                  <div
                    className={cn(
                      'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors',
                      isSelected
                        ? 'border-primary bg-primary'
                        : 'border-muted-foreground/30',
                    )}
                  >
                    {isSelected && (
                      <IconCheck
                        className="h-3 w-3 text-white"
                        strokeWidth={3}
                      />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {t(`steps.features.options.${featureKey}`)}
                      </span>
                      <Badge
                        variant={visual.badgeVariant}
                        size="sm"
                        className="font-semibold"
                      >
                        <PlanIcon />
                        {PLAN_DISPLAY_NAMES[minPlan]}
                      </Badge>
                    </div>
                    <p className="text-muted-foreground mt-0.5 text-xs">
                      {t(`steps.features.descriptions.${featureKey}`)}
                    </p>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </ResponsiveDialogBody>

      <ResponsiveDialogFooter>
        <Button variant="outline" onClick={onBack}>
          {t('actions.back')}
        </Button>
        <Button onClick={onNext}>{t('result.viewResults')}</Button>
      </ResponsiveDialogFooter>
    </>
  )
}
